import { alpha, createTheme } from "@mui/material/styles";
import { TypographyStyleOptions } from "@mui/material/styles/createTypography";

import { CustomPalette, FontFamilies, FontWeights } from "./enums";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h7: true;
    h8: true;
    error: true;
    subtitle3: true;
    subtitle4: true;
    subtitle5: true;
    subtitle6: true;
    subtitle7: true;
    subtitle8: true;
    subtitle9: true;
    subtitle10: true;
  }
}

declare module "@mui/material/styles/zIndex" {
  interface ZIndex {
    minimal: number;
    filterbar: number;
    selectMenuBox: number;
    textEditorIcon: number;
    loaderInTable: number;
  }
}

interface CustomShadows {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  dropdown: string;
  overlay: string;
  edge: string;
}

// Semantic aliases on top of the raw CustomPalette — say what a color is FOR
// (a muted label, a card surface, a default border) rather than which literal
// palette member it happens to be. Prefer these in new code; existing
// `theme.palette.custom.<raw_member>` call sites don't need to migrate.
// Add new aliases here as recurring "what is this color for" patterns show
// up during review — this list is intentionally small, not exhaustive.
const semanticPalette = {
  border: {
    default: CustomPalette.border_gray,
    subtle: CustomPalette.disable_gray,
  },
  text: {
    muted: CustomPalette.chart_axis_gray,
    disabled: CustomPalette.disable_text_gray,
  },
  surface: {
    card: CustomPalette.white,
    subtle: CustomPalette.dark_gray,
    hover: CustomPalette.pale_gray,
  },
} as const;

type SemanticPalette = typeof semanticPalette;

declare module "@mui/material/styles" {
  interface Theme {
    shape: {
      borderRadius: string[];
    };
    fontWeight: typeof FontWeights;
    customShadows: CustomShadows;
  }
  interface ThemeOptions {
    shape: {
      borderRadius: string[];
    };
    fontWeight: typeof FontWeights;
    customShadows: CustomShadows;
  }

  interface TypographyVariants {
    error: TypographyStyleOptions;
    h7: TypographyStyleOptions;
    h8: TypographyStyleOptions;
    subtitle3: TypographyStyleOptions;
    subtitle4: TypographyStyleOptions;
    subtitle5: TypographyStyleOptions;
    subtitle6: TypographyStyleOptions;
    subtitle7: TypographyStyleOptions;
    subtitle8: TypographyStyleOptions;
    subtitle9: TypographyStyleOptions;
    subtitle10: TypographyStyleOptions;
  }
  interface TypographyVariantsOptions {
    error: TypographyStyleOptions;
    h7: TypographyStyleOptions;
    h8: TypographyStyleOptions;
    subtitle3: TypographyStyleOptions;
    subtitle4: TypographyStyleOptions;
    subtitle5: TypographyStyleOptions;
    subtitle6: TypographyStyleOptions;
    subtitle7: TypographyStyleOptions;
    subtitle8: TypographyStyleOptions;
    subtitle9: TypographyStyleOptions;
    subtitle10: TypographyStyleOptions;
  }
  interface Palette {
    custom: typeof CustomPalette & { semantic: SemanticPalette };
  }
  interface PaletteOptions {
    custom: typeof CustomPalette & { semantic: SemanticPalette };
  }
}

export const theme = createTheme({
  zIndex: {
    minimal: 1,
    filterbar: 6,
    loaderInTable: 5,
    selectMenuBox: 2,
    textEditorIcon: 3,
  },
  components: {
    MuiFilledInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            borderTopLeftRadius: "unset",
            borderTopRightRadius: "unset",
          },
        },
      },
    },
  },
  fontWeight: {
    _400: FontWeights._400,
    _500: FontWeights._500,
    _600: FontWeights._600,
    _700: FontWeights._700,
    _800: FontWeights._800,
    _900: FontWeights._900,
    NORMAL: FontWeights.NORMAL,
  },
  // Index lookup, NOT a multiplier — theme.spacing(4) => "16px" (array
  // index 4), not 4 * 8px. New values are appended at the end so existing
  // theme.spacing(N) call sites keep resolving to the same pixel value.
  spacing: [0, 4, 8, 10, 16, 20, 24, 32, 40, 48, 64, 12],
  shape: {
    // Same index-lookup rule as spacing above — appended, not re-sorted.
    borderRadius: [3, 4, 10, 16, 20, 46, 8, 12].map((el) => `${el}px`),
  },
  customShadows: {
    xs: `0 2px 8px ${alpha(CustomPalette.black, 0.1)}`,
    sm: `0 4px 8px ${alpha(CustomPalette.black, 0.1)}`,
    md: `0px 5px 24px ${alpha(CustomPalette.black, 0.1)}`,
    lg: `0px 12px 24px ${alpha(CustomPalette.black, 0.1)}`,
    dropdown: `0px 2px 17px ${alpha(CustomPalette.light_dark, 0.15)}`,
    overlay: `0px 5px 29px ${alpha(CustomPalette.black, 0.5)}`,
    edge: `1px 0px 3px ${alpha(CustomPalette.black, 0.13)}`,
  },
  typography: {
    error: {
      fontSize: "12px",
      lineHeight: "14px",
    },
    h1: {
      fontWeight: FontWeights._700,
      fontSize: "31px",
      lineHeight: "36px",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      letterSpacing: "5px",
    },
    h2: {
      fontWeight: FontWeights._600,
      fontSize: "16px",
    },
    h3: {
      fontWeight: FontWeights._600,
      fontSize: "15px",
    },
    h4: {
      fontWeight: FontWeights._500,
      fontSize: "15px",
      lineHeight: "25px",
    },
    h5: {
      fontSize: "20px",
      fontWeight: FontWeights._600,
      lineHeight: "23px",
    },
    h6: {
      fontSize: "22px",
      fontWeight: FontWeights._500,
      color: CustomPalette.white,
      lineHeight: "1.6rem",
    },
    h7: {
      fontSize: "17px",
      fontWeight: FontWeights._500,
    },
    h8: {
      fontSize: "22px",
      fontWeight: FontWeights._600,
    },
    subtitle1: {
      fontSize: "18px",
      fontWeight: FontWeights._600,
    },
    subtitle2: {
      fontSize: "13px",
      fontWeight: FontWeights._500,
      color: CustomPalette.white,
      lineHeight: "15px",
    },
    subtitle3: {
      fontSize: "14px",
      fontWeight: FontWeights._400,
      color: CustomPalette.white,
    },
    subtitle4: {
      fontSize: "11px",
      fontWeight: FontWeights._600,
    },
    subtitle5: {
      fontSize: "13px",
      color: CustomPalette.white,
      fontWeight: FontWeights._600,
      lineHeight: "22px",
    },
    subtitle6: {
      fontSize: "11px",
      color: CustomPalette.white,
      fontWeight: FontWeights._400,
      lineHeight: "13px",
    },
    subtitle7: {
      fontSize: "13px",
      fontWeight: FontWeights._400,
    },
    subtitle8: {
      fontSize: "14px",
      fontWeight: FontWeights._500,
    },
    subtitle9: {
      fontSize: "12px",
      fontWeight: FontWeights._500,
    },
    subtitle10: {
      fontSize: "15px",
      fontWeight: FontWeights._500,
    },
    fontFamily: FontFamilies.WORK_SANS,
  },

  palette: {
    // CustomPalette is a string enum, so at runtime it already IS a
    // { key: value } object matching this shape — spreading it means a new
    // token only ever needs to be added in one place (enums.ts). `semantic`
    // layers named-by-purpose aliases on top — see semanticPalette above.
    custom: { ...CustomPalette, semantic: semanticPalette },
    text: {
      primary: CustomPalette.light_dark,
      secondary: CustomPalette.white,
      disabled: CustomPalette.disable_text_gray,
    },
    error: {
      main: CustomPalette.error_main,
    },
    primary: {
      main: CustomPalette.light_dark,
      contrastText: CustomPalette.white,
    },
    secondary: {
      main: CustomPalette.golden,
      contrastText: CustomPalette.light_dark,
    },
    info: {
      main: CustomPalette.white,
      contrastText: CustomPalette.light_dark,
    },
  },
});

export type CustomThemeType = typeof theme;
